// Outdoors screen data: USGS river gauges, NWS forecasts for the family's
// favorite places, and a plain-words fire danger rating. Pure helpers are
// shared with the Worker tests.

import { alertKind } from "./alerts";
import type { ForecastPeriod, OutdoorInterest, OutdoorSpot, SourceStamp, WeatherAlert } from "./models";
import type { SkyStamp } from "./sky/types";
import type { FireIndices } from "./weatherDetail";

export interface RiverGauge {
  siteId: string;
  name: string;
  distanceKm: number | null;
  /** Gauge position (absent in copies saved before it was added). */
  lat?: number;
  lon?: number;
  flowCfs: number | null;
  waterTempC: number | null;
  observedAt: string | null;
  /** Flow over the last ~3 hours. */
  trend: "rising" | "falling" | "steady" | null;
  /** Hourly flow over the last 12 hours, oldest first. */
  history: number[];
}

export interface RiversPayload {
  gauges: RiverGauge[];
  stamp: SkyStamp;
}

export interface SpotForecast {
  spot: OutdoorSpot;
  /** The next few NWS forecast periods (day/night). */
  periods: ForecastPeriod[];
  error: string | null;
}

export interface SpotsPayload {
  spots: SpotForecast[];
  fetchedAt: string;
}

export const OUTDOOR_INTERESTS: { key: OutdoorInterest; label: string }[] = [
  { key: "biking", label: "Mountain biking" },
  { key: "hiking", label: "Hiking" },
  { key: "hunting", label: "Hunting" },
  { key: "fishing", label: "Fishing" },
  { key: "paddling", label: "Paddling" },
  { key: "camping", label: "Camping" },
  { key: "skiing", label: "Skiing" },
];

/** Rain and temperature over the last three days at the home weather station. */
export interface GroundPayload {
  stationId: string;
  /** Clock hours in the last 72 with at least one report. */
  hoursReported: number;
  /** Totals in mm; null when the station doesn't measure precipitation. */
  rain24Mm: number | null;
  rain48Mm: number | null;
  rain72Mm: number | null;
  /** Hours since the last hour with measurable precipitation; null = none in 72 h. */
  hoursSinceWet: number | null;
  /** Some of it fell at or near freezing (snow, sleet or a freeze-up). */
  frozen: boolean;
  low24C: number | null;
  high24C: number | null;
  stamp: SourceStamp;
}

export interface StationReport {
  t: number;
  precipMm: number | null;
  tempC: number | null;
}

/** Measurable precipitation, mm (0.01″). */
const WET_MM = 0.25;

/**
 * Totals from a station's reports. Routine METARs carry the last hour's
 * precipitation and specials the amount since the last routine report, so
 * each clock hour counts its largest value once.
 */
export function summarizeGround(reports: StationReport[], now: number): Omit<GroundPayload, "stationId" | "stamp"> {
  const HOUR = 3_600_000;
  const hours = new Map<number, { precip: number | null; temp: number | null }>();
  for (const r of reports) {
    if (r.t > now || r.t < now - 72 * HOUR) continue;
    const k = Math.floor(r.t / HOUR);
    const h = hours.get(k) ?? { precip: null, temp: null };
    if (r.precipMm !== null) h.precip = Math.max(h.precip ?? 0, r.precipMm);
    if (r.tempC !== null) h.temp = h.temp === null ? r.tempC : Math.min(h.temp, r.tempC);
    hours.set(k, h);
  }
  const measures = [...hours.values()].some((h) => h.precip !== null);
  const nowH = Math.floor(now / HOUR);
  const total = (within: number) => (measures ? Math.round([...hours].filter(([k]) => nowH - k < within).reduce((s, [, h]) => s + (h.precip ?? 0), 0) * 10) / 10 : null);
  const wet = [...hours].filter(([, h]) => (h.precip ?? 0) >= WET_MM);
  const lastWet = wet.length ? Math.max(...wet.map(([k]) => k)) : null;
  const temps24 = [...hours].filter(([k, h]) => nowH - k < 24 && h.temp !== null).map(([, h]) => h.temp!);
  return {
    hoursReported: hours.size,
    rain24Mm: total(24),
    rain48Mm: total(48),
    rain72Mm: total(72),
    hoursSinceWet: lastWet === null ? null : Math.max(0, nowH - lastWet),
    frozen: wet.some(([, h]) => h.temp !== null && h.temp <= 1),
    low24C: temps24.length ? Math.min(...temps24) : null,
    high24C: temps24.length ? Math.max(...temps24) : null,
  };
}

/** "SOUTH PLATTE R AT 64TH AVE. COMMERCE CITY, CO." → "South Platte River at 64th Ave. Commerce City" */
export function gaugeName(raw: string): string {
  const small = new Set(["at", "near", "above", "below", "of", "and", "the", "in"]);
  const expand: Record<string, string> = { r: "River", cr: "Creek", crk: "Creek", ck: "Creek", nr: "near", abv: "above", bel: "below", blw: "below", trib: "Tributary" };
  const words = raw
    .replace(/,?\s*(CO|COLO|COLORADO)\.?\s*$/i, "")
    .trim()
    .split(/\s+/)
    .map((w, i) => {
      const lower = w.toLowerCase();
      const bare = lower.replace(/[.,]$/, "");
      if (expand[bare]) return expand[bare] + (lower.endsWith(",") ? "," : "");
      if (i > 0 && small.has(bare)) return lower;
      if (/^\d/.test(w)) return lower;
      return lower.charAt(0).toUpperCase() + lower.slice(1);
    });
  return words.join(" ").replace(/,$/, "");
}

/** Rising/falling when flow changed by more than 10% (and 1 cfs) over the window. */
export function flowTrend(then: number | null, now: number | null): RiverGauge["trend"] {
  if (then === null || now === null) return null;
  const d = now - then;
  if (Math.abs(d) < Math.max(1, Math.abs(then) * 0.1)) return "steady";
  return d > 0 ? "rising" : "falling";
}

export type FireLevel = 1 | 2 | 3 | 4 | 5;

export const FIRE_LEVELS: Record<FireLevel, { name: string; color: string; advice: string }> = {
  1: { name: "Low", color: "#4fb38a", advice: "Fires are unlikely to spread far." },
  2: { name: "Moderate", color: "#5aa7e0", advice: "Be careful with anything that sparks." },
  3: { name: "High", color: "#f0b44d", advice: "Grass fires can start easily and spread fast. Hold off on open flames." },
  4: { name: "Very high", color: "#ff7a1a", advice: "No campfires, fireworks or outdoor burning. Park off dry grass." },
  5: { name: "Extreme", color: "#e03131", advice: "Any spark can start a fast-moving fire. Know your way out and keep phones charged for evacuation alerts." },
};

export interface FireDanger {
  level: FireLevel;
  /** Plain-words reasons, most important first. */
  reasons: string[];
  /** Fire weather alerts in effect (red flag warnings, fire weather watches). */
  alerts: WeatherAlert[];
}

/**
 * Fire danger at home, from NWS fire weather alerts and the gridpoint
 * forecast: humidity, gusts, the grassland fire danger index and Haines.
 * An official Red Flag Warning always means the top level.
 */
export function fireDanger(alerts: WeatherAlert[], fire: FireIndices | null, mph: (kph: number) => number = (k) => k / 1.609344): FireDanger {
  const fireAlerts = alerts.filter((a) => alertKind(a.event) === "fire");
  const reasons: string[] = [];
  let level: FireLevel = 1;
  const bump = (l: FireLevel) => {
    if (l > level) level = l;
  };
  for (const a of fireAlerts) {
    if (/red flag|fire warning|extreme fire/i.test(a.event)) {
      bump(5);
      reasons.push(`${a.event} in effect`);
    } else if (/fire weather watch/i.test(a.event)) {
      bump(4);
      reasons.push("Fire Weather Watch: red flag conditions possible");
    }
  }
  if (fire) {
    const rh = fire.minRh;
    const gustMph = fire.maxGustKph === null ? null : mph(fire.maxGustKph);
    if (rh !== null) {
      if (rh < 10) bump(4);
      else if (rh < 15) bump(3);
      else if (rh < 25) bump(2);
      if (rh < 25) reasons.push(`Very dry air: humidity down to ${Math.round(rh)}%`);
    }
    if (gustMph !== null && gustMph >= 25) {
      bump(rh !== null && rh < 15 ? 4 : 3);
      reasons.push(`Gusty wind up to ${Math.round(gustMph)} mph`);
    }
    if (rh !== null && rh < 15 && gustMph !== null && gustMph >= 35) bump(5);
    if (fire.grasslandMax !== null && fire.grasslandMax >= 3) {
      bump(Math.min(5, fire.grasslandMax) as FireLevel);
      reasons.push(`NWS grassland fire danger: ${["", "low", "moderate", "high", "very high", "extreme"][Math.min(5, fire.grasslandMax)]}`);
    }
    if (fire.hainesMax !== null && fire.hainesMax >= 5) {
      bump(level >= 2 ? (Math.min(5, level + 1) as FireLevel) : 2);
      reasons.push("Dry, unstable air (Haines 5–6) would let a fire grow quickly");
    }
  }
  if (reasons.length === 0) reasons.push(fire ? "Humidity and wind look calm" : "Forecast details unavailable");
  return { level, reasons, alerts: fireAlerts };
}
